import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from './useAuth'

function getSupabaseFunctionsUrl(): string {
  const isDev = import.meta.env.MODE === 'development'
  const useProdSupabase = import.meta.env.VITE_USE_PROD_SUPABASE === 'true'
  const useDevConfig = isDev && !useProdSupabase

  const supabaseUrl = useDevConfig
    ? import.meta.env.VITE_DEV_SUPABASE_URL
    : import.meta.env.VITE_SUPABASE_URL

  return `${supabaseUrl}/functions/v1`
}

export function useCreateOrg() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (name: string) => {
      if (!user) throw new Error('Not authenticated')

      // Create the organization (trigger auto-adds owner as member)
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .insert({ name, owner_id: user.id })
        .select('id')
        .single()

      if (orgError) {
        throw new Error(`Failed to create organization: ${orgError.message}`)
      }

      return org
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org'] })
      queryClient.invalidateQueries({ queryKey: ['user-orgs'] })
      queryClient.invalidateQueries({ queryKey: ['org-members'] })
    },
  })
}

export function useDeleteOrg() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (orgId: string) => {
      if (!user) throw new Error('Not authenticated')

      const { error } = await supabase
        .from('organizations')
        .delete()
        .eq('id', orgId)
        .eq('owner_id', user.id)

      if (error) {
        throw new Error(`Failed to delete organization: ${error.message}`)
      }
    },
    onMutate: async (orgId: string) => {
      await queryClient.cancelQueries({ queryKey: ['user-orgs'] })

      const prevOrgs = queryClient.getQueryData(['user-orgs', user?.id])
      const orgs = (prevOrgs ?? []) as Array<{ id: string }>

      queryClient.setQueryData(
        ['user-orgs', user?.id],
        orgs.filter((o) => o.id !== orgId),
      )

      return { prevOrgs }
    },
    onError: (_err, _orgId, context) => {
      if (context?.prevOrgs !== undefined) {
        queryClient.setQueryData(['user-orgs', user?.id], context.prevOrgs)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['user-orgs'] })
      queryClient.invalidateQueries({ queryKey: ['org'] })
      queryClient.invalidateQueries({ queryKey: ['org-members'] })
    },
  })
}

export function useLeaveOrg() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (orgId: string) => {
      if (!user) throw new Error('Not authenticated')

      const { error } = await supabase
        .from('organization_members')
        .update({ deleted_at: new Date().toISOString() })
        .eq('org_id', orgId)
        .eq('user_id', user.id)

      if (error) {
        throw new Error(`Failed to leave organization: ${error.message}`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-orgs'] })
      queryClient.invalidateQueries({ queryKey: ['org'] })
      queryClient.invalidateQueries({ queryKey: ['org-members'] })
    },
  })
}

export function useInviteMember(orgId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (email: string) => {
      if (!orgId) throw new Error('No organization')

      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      const functionsUrl = getSupabaseFunctionsUrl()
      const response = await fetch(`${functionsUrl}/invite-to-org`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ org_id: orgId, email }),
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to send invitation')
      }

      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-invitations'] })
    },
  })
}

export function useAcceptInvitation() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { data, error } = await supabase.rpc('accept_org_invitation', {
        invitation_id: invitationId,
      })

      if (error) {
        throw new Error(`Failed to accept invitation: ${error.message}`)
      }

      return data
    },
    onMutate: async (invitationId: string) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['received-invitations'] })
      await queryClient.cancelQueries({ queryKey: ['user-orgs'] })

      // Snapshot previous values for rollback
      const prevInvitations = queryClient.getQueryData(['received-invitations', user?.id])
      const prevOrgs = queryClient.getQueryData(['user-orgs', user?.id])

      // Find the invitation being accepted so we can move it to the orgs list
      const invitations = (prevInvitations ?? []) as Array<{
        id: string; orgId: string; orgName: string; status: string; expiresAt: string; createdAt: string
      }>
      const accepted = invitations.find((inv) => inv.id === invitationId)

      // Optimistically remove from received invitations
      queryClient.setQueryData(
        ['received-invitations', user?.id],
        invitations.filter((inv) => inv.id !== invitationId),
      )

      // Optimistically add to user orgs
      if (accepted) {
        const currentOrgs = (prevOrgs ?? []) as Array<{ id: string; name: string; role: string }>
        queryClient.setQueryData(
          ['user-orgs', user?.id],
          [...currentOrgs, { id: accepted.orgId, name: accepted.orgName, role: 'member' }],
        )
      }

      return { prevInvitations, prevOrgs }
    },
    onError: (_err, _invitationId, context) => {
      // Rollback on error
      if (context?.prevInvitations !== undefined) {
        queryClient.setQueryData(['received-invitations', user?.id], context.prevInvitations)
      }
      if (context?.prevOrgs !== undefined) {
        queryClient.setQueryData(['user-orgs', user?.id], context.prevOrgs)
      }
    },
    onSettled: () => {
      // Refetch to get the real server state
      queryClient.invalidateQueries({ queryKey: ['org'] })
      queryClient.invalidateQueries({ queryKey: ['user-orgs'] })
      queryClient.invalidateQueries({ queryKey: ['received-invitations'] })
      queryClient.invalidateQueries({ queryKey: ['org-members'] })
    },
  })
}

export function useCancelInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase
        .from('org_invitations')
        .update({ status: 'cancelled' })
        .eq('id', invitationId)

      if (error) {
        throw new Error(`Failed to cancel invitation: ${error.message}`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-invitations'] })
    },
  })
}

export function useRemoveMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase
        .from('organization_members')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', memberId)

      if (error) {
        throw new Error(`Failed to remove member: ${error.message}`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-members'] })
    },
  })
}

export function useDeclineInvitation() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase.rpc('decline_org_invitation', {
        invitation_id: invitationId,
      })

      if (error) {
        throw new Error(`Failed to decline invitation: ${error.message}`)
      }
    },
    onMutate: async (invitationId: string) => {
      await queryClient.cancelQueries({ queryKey: ['received-invitations'] })

      const prevInvitations = queryClient.getQueryData(['received-invitations', user?.id])
      const invitations = (prevInvitations ?? []) as Array<{ id: string }>

      queryClient.setQueryData(
        ['received-invitations', user?.id],
        invitations.filter((inv) => inv.id !== invitationId),
      )

      return { prevInvitations }
    },
    onError: (_err, _invitationId, context) => {
      if (context?.prevInvitations !== undefined) {
        queryClient.setQueryData(['received-invitations', user?.id], context.prevInvitations)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['received-invitations'] })
    },
  })
}
